import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, FileEdit, BarChart3, GitCompareArrows, CalendarDays, Briefcase, Lock,
  Activity, MessageSquare, Building2, ArrowRight, FileCheck2, Gauge, Loader2, FolderOpen, Package,
} from 'lucide-react';
import PageHelpButton from './PageHelpButton';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tabs hidden from the view-only USER role (v273): Compare, Quote,
// Design Pack, Requirements, Documents, Maturity, Activity, Comments.
const VIEWER_HIDDEN_TABS = new Set([
  'requirements', 'documents', 'maturity', 'compare', 'quote',
  'design-pack', 'activity', 'comments',
]);

// Maps each workspace tab to the most relevant User Guide section anchor
// so the context-aware "?" deep-links straight to just-in-time help.
const TAB_GUIDE_SECTION = {
  requirements: 'requirements',
  maturity: 'maturity',
  conditions: 'project-conditions',
  estimate: 'estimate-project-factors-page',
  planner: 'planner',
  overview: 'full-report',
  compare: 'compare',
  quote: 'quote',
  'design-pack': 'design-pack',
  activity: 'activity',
  comments: 'comments',
  documents: 'requirements',
};

// Tab order matches the natural project flow:
//   Customer Org → Requirements → Maturity → Project Conditions → Estimate → … → Comments
// Project Conditions sits between Maturity and Estimate as a
// non-route flyout button (handled separately in the render).
const NAV_TABS = [
  { id: 'requirements', label: 'Requirements', icon: FileText, getPath: (id) => `/estimator/${id}/requirements` },
  // v260.134 — Source Documents: the raw spec docs that informed the
  // requirements. Sits right after Requirements as a first-class tab.
  { id: 'documents', label: 'Documents', icon: FolderOpen, getPath: (id) => `/workspace/${id}/documents` },
  // v260.117 — Maturity is its own first-class tab so the per-estimate
  // assessment lives alongside Requirements rather than off in /maturity.
  { id: 'maturity', label: 'Maturity', icon: Gauge, getPath: (id) => `/estimator/${id}/maturity` },
  // 'conditions' is injected here at render time (modal trigger, not a route)
  { id: 'estimate', label: 'Estimate', icon: FileEdit, getPath: (id) => `/estimator?load=${id}` },
  { id: 'planner', label: 'Planner', icon: CalendarDays, getPath: (id) => `/workspace/${id}/planner` },
  { id: 'overview', label: 'Full Report', icon: BarChart3, getPath: (id) => `/workspace/${id}/overview` },
  { id: 'compare', label: 'Compare', icon: GitCompareArrows, getPath: (id) => `/workspace/${id}/compare` },
  { id: 'product-pricing', label: 'Product Pricing', icon: Package, getPath: (id) => `/workspace/${id}/product-pricing` },
  { id: 'quote', label: 'Quote', icon: Briefcase, getPath: (id) => `/workspace/${id}/quote` },
  { id: 'design-pack', label: 'Design Pack', icon: FileCheck2, getPath: (id) => `/workspace/${id}/design-pack` },
  { id: 'activity', label: 'Activity', icon: Activity, getPath: (id) => `/workspace/${id}/activity` },
  { id: 'comments', label: 'Comments', icon: MessageSquare, getPath: (id) => `/workspace/${id}/comments` },
];

// Map progress state → dot colour. `null` / `undefined` skips the dot.
const STATE_DOT = {
  complete: 'bg-emerald-500',
  partial:  'bg-amber-400',
  empty:    'bg-slate-200',
};

const STATE_TITLE = {
  complete: 'Complete',
  partial:  'In progress',
  empty:    'Not started',
};

/**
 * Derive a per-tab progress map from a hydrated estimate document.
 *   complete  → ✅ rep can move on
 *   partial   → 🟡 started but more to do
 *   empty     → ⚪ not started yet
 *   undefined → no dot rendered (Activity / Comments / Conditions)
 *
 * Exported so callers that already have the estimate (Workspace
 * Overview, Estimator Step 4, Requirements page, Output page) can
 * pass the same shape to the sub-nav.
 */
export function computeEstimateProgress(estimate) {
  if (!estimate) return {};
  const rd = estimate.requirements_doc || null;
  const results = estimate.results || {};
  const provCount = rd ? Object.keys(rd.field_provenance || {}).length : 0;
  const verifiedCount = rd ? Object.keys(rd.field_verifications || {}).length : 0;
  // Count requirement sections that have ANY non-empty top-level field
  // (a quick proxy for "doc has real content" beyond default scaffolding).
  let touchedSections = 0;
  if (rd) {
    for (const [k, v] of Object.entries(rd)) {
      if (k === 'updated_at' || k === 'updated_by' || k === 'field_provenance' || k === 'field_verifications') continue;
      if (v && typeof v === 'object') {
        const hasContent = Object.values(v).some(x => x != null && x !== '' && !(Array.isArray(x) && x.length === 0));
        if (hasContent) touchedSections += 1;
      }
    }
  }

  // Requirements completion has two signal paths:
  //   1. Full estimate doc available → analyze requirements_doc directly
  //      (AI imports verified + section content).
  //   2. List/summary payload → use the flat has_requirements +
  //      requirements_applied_at flags surfaced by /api/workspaces/summary
  //      so the dashboard chip still reflects reality without a per-row
  //      detail fetch.
  let requirements;
  if (rd) {
    if (provCount > 0 && verifiedCount >= provCount) {
      requirements = 'complete';
    } else if (provCount > 0 || touchedSections >= 2) {
      requirements = 'partial';
    } else {
      requirements = 'empty';
    }
  } else if (estimate.has_requirements) {
    requirements = estimate.requirements_applied_at ? 'complete' : 'partial';
  } else {
    requirements = 'empty';
  }

  // Results / cost completeness — accept both shapes:
  //   • Full estimate doc:      results.total_cost
  //   • Summary list payload:   top-level total_cost
  const hasResults = Number(results.total_cost ?? estimate.total_cost ?? 0) > 0;

  return {
    requirements,
    // v260.117 — Maturity progress: 'complete' if the estimate's org has
    // an assessment baked in, 'empty' otherwise. The backend sets
    // `estimate.has_maturity_assessment` based on the org's latest record.
    maturity: estimate.has_maturity_assessment ? 'complete' : 'empty',
    conditions: estimate.has_conditions ? 'complete' : 'empty',
    estimate: hasResults ? 'complete' : 'empty',
    planner: estimate.latest_plan_id ? 'complete' : 'empty',
    overview: hasResults ? 'complete' : 'empty',
    compare: estimate.last_compared_at ? 'complete' : 'empty',
    quote: (estimate.scope_pack_sent_at || estimate.quote_sent_at)
      ? 'complete'
      : 'empty',
    // Activity / Comments are meta surfaces, not flow steps — no dot.
  };
}

/**
 * Workspace sub-nav rendered above every estimate surface (Estimator
 * Step 4, EstimateOutputPage, Requirements form, and every
 * /workspace/{id}/* route).
 *
 * Two new behaviours (v260.26):
 *   • A persistent customer · project context strip above the tabs
 *     so the user never loses sight of WHICH deal they're editing.
 *   • A small status dot to the left of each tab label indicating
 *     completion progress for that step.
 *
 * Props:
 *   estimateId               - required for route-based tabs
 *   activeTab                - id of the current tab (highlighted)
 *   hasConditions            - controls enabled state of the PC tab
 *   onProjectConditionsClick - handler when PC tab clicked (enabled)
 *   onNavigate               - optional override (used by Estimator
 *                              save-gate to interpose a save dialog)
 *   customerName             - top-strip "Customer" label
 *   projectName              - top-strip "Project" label
 *   progress                 - { [tabId]: 'complete'|'partial'|'empty' }
 */
export default function WorkspaceSubNav({
  estimateId,
  activeTab,
  hasConditions = false,
  onProjectConditionsClick,
  onNavigate,
  customerName = null,
  projectName = null,
  progress = {},
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isViewer = user?.role === 'user';
  const visibleTabs = isViewer ? NAV_TABS.filter(t => !VIEWER_HIDDEN_TABS.has(t.id)) : NAV_TABS;

  // v260.132 — Poll the latest transcript-parse job for this estimate so a
  // long background analysis surfaces a dot on the Requirements tab from
  // ANY surface in the estimate (Planner, Quote, …). 'processing' shows a
  // spinner; 'done' (un-consumed) shows a pulsing "ready" dot.
  const [txStatus, setTxStatus] = useState(null);
  useEffect(() => {
    if (!estimateId) return undefined;
    let mounted = true;
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/estimates/${estimateId}/requirements/parse-transcript/jobs-latest`,
          { withCredentials: true },
        );
        const st = r.data?.job?.status || null;
        if (!mounted) return;
        setTxStatus(st === 'processing' || st === 'done' ? st : null);
        if (st === 'processing') timer = setTimeout(poll, 6000);
      } catch { /* non-blocking */ }
    };
    poll();
    return () => { mounted = false; if (timer) clearTimeout(timer); };
  }, [estimateId]);

  if (!estimateId) return null;

  const handleNavClick = (tab) => {
    if (onNavigate) {
      onNavigate(tab);
      return;
    }
    navigate(tab.getPath(estimateId));
  };

  const renderDot = (state) => {
    if (!state) return null;
    const cls = STATE_DOT[state] || STATE_DOT.empty;
    return (
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full ${cls}`}
        title={STATE_TITLE[state] || ''}
        data-testid={`subnav-dot-state-${state}`}
      />
    );
  };

  return (
    <div className="bg-white border-b border-slate-200" data-testid="workspace-subnav">
      {/* Customer / Project context strip — only when we know both bits */}
      {(customerName || projectName) && (
        <div
          className="bg-slate-50 border-b border-slate-100"
          data-testid="workspace-subnav-context"
        >
          <div className="w-full px-4 sm:px-6 lg:px-8 py-1.5 flex items-center gap-2 text-[11px]">
            <Building2 className="w-3 h-3 text-slate-400 shrink-0" />
            <span className="text-slate-400 uppercase tracking-wider text-[9px] font-semibold">Working on</span>
            {customerName && (
              <span
                className="font-medium text-[#0F2040] truncate max-w-[220px]"
                title={customerName}
                data-testid="workspace-subnav-customer"
              >
                {customerName}
              </span>
            )}
            {customerName && projectName && (
              <span className="text-slate-300" aria-hidden>/</span>
            )}
            {projectName && (
              <span
                className="font-semibold text-cyan-700 truncate max-w-[320px]"
                title={projectName}
                data-testid="workspace-subnav-project"
              >
                {projectName}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="w-full px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center gap-0.5 -mb-px">
          {visibleTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const state = progress?.[tab.id];
            const node = (
              <button
                key={tab.id}
                onClick={() => handleNavClick(tab)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-all ${
                  isActive
                    ? 'border-[#4ABFCF] text-[#0F2040]'
                    : 'border-transparent text-slate-500 hover:text-[#0F2040] hover:border-slate-300'
                }`}
                data-testid={`subnav-${tab.id}`}
              >
                {renderDot(state)}
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
                {tab.id === 'requirements' && txStatus === 'processing' && (
                  <span className="ml-1 inline-flex" title="Analyzing transcript…" data-testid="subnav-transcript-processing">
                    <Loader2 className="w-3 h-3 text-cyan-500 animate-spin" />
                  </span>
                )}
                {tab.id === 'requirements' && txStatus === 'done' && (
                  <span
                    className="ml-1 inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"
                    title="Transcript ready to review"
                    data-testid="subnav-transcript-ready"
                  />
                )}
              </button>
            );

            // Inject the Project Conditions button right before the
            // Estimate tab so the sub-nav reads as:
            //   Requirements · Documents · Maturity · Project Conditions · Estimate · …
            if (tab.id === 'estimate') {
              const isPCActive = activeTab === 'conditions';
              const pcState = progress?.conditions;
              const isLegacy = !hasConditions;
              return (
                <React.Fragment key="frag-conditions">
                  <button
                    onClick={() => onProjectConditionsClick && onProjectConditionsClick()}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-all ${
                      isPCActive
                        ? 'border-[#4ABFCF] text-[#0F2040]'
                        : isLegacy
                          ? 'border-transparent text-amber-700 hover:text-amber-900 hover:border-amber-300'
                          : 'border-transparent text-slate-500 hover:text-[#0F2040] hover:border-slate-300'
                    }`}
                    data-testid="subnav-conditions"
                    title={isLegacy
                      ? 'Migrate this estimate to the Conditions Flow'
                      : 'Edit Project Conditions'}
                  >
                    {renderDot(pcState)}
                    <Lock className="w-3.5 h-3.5" />
                    Project Conditions
                    {isLegacy && (
                      <span
                        className="ml-1 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold text-amber-600 bg-amber-50 border border-amber-200 px-1 py-px rounded"
                        data-testid="subnav-conditions-migrate-badge"
                      >
                        <ArrowRight className="w-2.5 h-2.5" />
                        Migrate
                      </span>
                    )}
                  </button>
                  {node}
                </React.Fragment>
              );
            }
            return node;
          })}
          {/* Context-aware help — deep-links to the guide section for the active tab */}
          <div className="ml-auto flex items-center pl-2">
            <PageHelpButton
              section={TAB_GUIDE_SECTION[activeTab] || '3-the-estimate-workspace'}
              label="Open the user guide for this tab"
              testId="subnav-help"
            />
          </div>
        </nav>
      </div>
    </div>
  );
}
